import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCan, useCompanies, useSearch } from '../api/hooks';
import type { SearchResult } from '../api/types';
import { TYPES } from '../lib/doc';
import { dt, money } from '../lib/format';
import { useCompanyFilter } from '../state/company';
import { I } from './Icons';

/**
 * Command palette + globální hledání (kap. 37, 38): Ctrl+K / Cmd+K / „/" nebo lupa v topbaru.
 * Nahoře akce (Nová faktura, Platby, …), pod nimi výsledky po skupinách; šipky, Enter, Esc.
 * Dotaz umí časové fráze („faktury Novák minulý měsíc") – server je rozpozná a vrátí popis.
 */

interface Entry {
  key: string;
  group: string;
  title: string;
  sub?: string;
  right?: string;
  /** right je klávesová zkratka – na dotykovém zařízení se neukazuje */
  shortcut?: boolean;
  run: () => void;
}

const ACTIONS: { title: string; sub: string; path: string; keys: string[]; permission?: string }[] =
  [
    {
      title: 'Nová faktura',
      sub: 'vystavit doklad',
      path: '/novy',
      keys: ['n'],
      permission: 'documents:issue',
    },
    { title: 'Přehled', sub: 'dashboard', path: '/', keys: ['g', 'h'] },
    {
      title: 'AI asistent',
      sub: 'faktury, sklad, schůzky a přehledy větou',
      path: '/asistent',
      keys: ['g', 'a'],
    },
    {
      title: 'Kalendář',
      sub: 'schůzky, výkupy, předání, odběr do iPhonu',
      path: '/kalendar',
      keys: ['g', 'c'],
    },
    {
      title: 'Úkoly mozku',
      sub: 'co má AI dělat sama – denně, týdně, jednou',
      path: '/ukoly',
      keys: ['g', 't'],
    },
    {
      title: 'Reklamy',
      sub: 'Meta Ads – útrata, objednávky z webů, ROAS',
      path: '/reklamy',
      keys: ['g', 'm'],
      permission: 'reports:read',
    },
    { title: 'Doklady', sub: 'seznam a filtry', path: '/doklady', keys: ['g', 'd'] },
    { title: 'Platby', sub: 'párování, výpisy', path: '/platby', keys: ['g', 'p'] },
    {
      title: 'Zakázky',
      sub: 'opravy, termíny, k vyzvednutí',
      path: '/zakazky',
      keys: ['g', 'z'],
      permission: 'jobs:read',
    },
    {
      title: 'Weby',
      sub: 'návštěvnost, zdroje, regiony, zákazníci e-shopů',
      path: '/weby',
      keys: ['g', 'w'],
      permission: 'web:read',
    },
    {
      title: 'Nová zakázka',
      sub: 'přijmout zařízení do opravy',
      path: '/zakazky/nova',
      keys: [],
      permission: 'jobs:write',
    },
    { title: 'Sklad', sub: 'zboží, kusy, pohyby', path: '/sklad', keys: ['g', 's'] },
    {
      title: 'Velkoobchod',
      sub: 'nákupy, co zbývá prodat, odběratelé',
      path: '/velkoobchod',
      keys: [],
    },
    {
      title: 'Přehledy',
      sub: 'obrat, marže, cash flow, pohledávky',
      path: '/prehledy',
      keys: ['g', 'r'],
    },
    { title: 'Účetní', sub: 'DPH, hlášení, balíček', path: '/vice/ucetni', keys: ['g', 'u'] },
    { title: 'Kontakty', sub: 'odběratelé a dodavatelé', path: '/vice/kontakty', keys: ['g', 'k'] },
    {
      title: 'Nastavení',
      sub: 'firmy, řady, uživatelé',
      path: '/vice/nastaveni',
      keys: ['g', 'n'],
    },
    {
      title: 'Návody',
      sub: 'jak funguje která funkce a kde ji najdeš',
      path: '/vice/navody',
      keys: ['g', 'v'],
    },
  ];

function strip(s: string) {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { activeCompanyId, isAll } = useCompanyFilter();
  const { data: companies = [] } = useCompanies();
  const companyId = isAll ? null : activeCompanyId;
  const access = useCan(companyId);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const { data, isFetching } = useSearch(debounced, companyId);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 180);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (open) {
      setQ('');
      setDebounced('');
      setCursor(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const entries = useMemo(() => {
    const go = (path: string) => () => {
      onClose();
      navigate(path);
    };
    const needle = strip(q.trim());
    const list: Entry[] = [];
    for (const a of ACTIONS) {
      if (a.permission && access.loaded && !access.can(a.permission as never)) continue;
      if (needle && !strip(`${a.title} ${a.sub}`).includes(needle)) continue;
      list.push({
        key: `a:${a.path}`,
        group: 'Akce',
        title: a.title,
        sub: a.sub,
        right: a.keys.join(' '),
        shortcut: true,
        run: go(a.path),
      });
    }
    if (needle && companies.length > 1) {
      for (const c of companies) {
        if (!strip(c.shortName).includes(needle) && !strip(c.name).includes(needle)) continue;
        list.push({
          key: `c:${c.id}`,
          group: 'Firmy',
          title: c.shortName,
          sub: 'přepnout firmu',
          run: () => {
            onClose();
            try {
              localStorage.setItem('isupply.company', c.id);
            } catch {
              /* bez localStorage */
            }
            window.location.assign('/');
          },
        });
      }
    }
    const r: SearchResult | undefined = data;
    if (r && debounced.trim().length >= 2) {
      for (const d of r.documents) {
        list.push({
          key: `d:${d.id}`,
          group: 'Doklady',
          title: `${d.number ?? 'Koncept'} · ${d.customerName}`,
          sub: `${TYPES[d.type]?.label ?? d.type} · ${dt(d.issuedAt)}`,
          right: money(d.totalAmount, d.currency ?? 'CZK'),
          run: go(`/doklady/${d.id}`),
        });
      }
      for (const j of r.jobs ?? []) {
        list.push({
          key: `z:${j.id}`,
          group: 'Zakázky',
          title: `${j.number} · ${j.customerName}`,
          sub: [j.deviceName || j.title, j.statusLabel].filter(Boolean).join(' · '),
          run: go(`/zakazky/${j.id}`),
        });
      }
      for (const c of r.contacts) {
        list.push({
          key: `k:${c.id}`,
          group: 'Kontakty',
          title: c.name,
          sub: [c.ico ? `IČO ${c.ico}` : '', c.email ?? ''].filter(Boolean).join(' · '),
          run: go(`/vice/kontakty/${c.id}`),
        });
      }
      for (const p of r.products) {
        list.push({
          key: `p:${p.id}`,
          group: 'Produkty',
          title: p.name,
          sub:
            [p.sku, p.ean].filter(Boolean).join(' · ') ||
            (p.tracksSerial ? 'po kusech' : 'množství'),
          run: go(`/sklad?productId=${p.id}`),
        });
      }
      for (const u of r.units) {
        list.push({
          key: `u:${u.id}`,
          group: 'Kusy',
          title: u.serial,
          sub: `${u.product} · ${u.warehouse} · ${u.status}`,
          run: go(`/sklad?serial=${u.serial}`),
        });
      }
      for (const p of r.payments) {
        list.push({
          key: `y:${p.id}`,
          group: 'Platby',
          title: `${money(p.amount, p.currency)} · ${p.payerName || 'bez plátce'}`,
          sub: `${dt(p.date)}${p.variableSymbol ? ` · VS ${p.variableSymbol}` : ''}${p.documentNumber ? ` · ${p.documentNumber}` : ''}`,
          run: go('/platby'),
        });
      }
      for (const u of r.users) {
        list.push({
          key: `m:${u.id}`,
          group: 'Uživatelé',
          title: u.email,
          sub: u.role,
          run: go('/vice/nastaveni'),
        });
      }
    }
    return list;
  }, [q, debounced, data, access, companies, navigate, onClose]);

  useEffect(() => {
    if (cursor >= entries.length) setCursor(Math.max(0, entries.length - 1));
  }, [entries, cursor]);

  if (!open) return null;

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(entries.length - 1, c + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(0, c - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      entries[cursor]?.run();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  const period = data?.query.periodLabel;
  let lastGroup = '';

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: klávesy obsluhuje vstupní pole
    <div className="palette-bg" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="palette" role="dialog" aria-modal="true" aria-label="Hledání a akce">
        <div className="palette-in">
          {I.search}
          <input
            ref={inputRef}
            type="text"
            placeholder="Hledej doklad, kontakt, IMEI, platbu… nebo napiš akci"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            aria-label="Hledání"
            autoComplete="off"
          />
          <kbd>Esc</kbd>
          <button type="button" className="palette-close" onClick={onClose}>
            Zavřít
          </button>
        </div>
        {period && debounced && (
          <div className="palette-meta">
            Období {period}
            {data?.query.text ? ` · „${data.query.text}"` : ''}
          </div>
        )}
        <div className="palette-list" role="listbox">
          {entries.map((en, i) => {
            const head = en.group !== lastGroup ? en.group : null;
            lastGroup = en.group;
            return (
              <div key={en.key}>
                {head && <div className="palette-group">{head}</div>}
                <button
                  type="button"
                  role="option"
                  aria-selected={i === cursor}
                  className={`palette-item ${i === cursor ? 'on' : ''}`.trim()}
                  onMouseEnter={() => setCursor(i)}
                  onClick={en.run}
                >
                  <span className="grow">
                    <span className="l1">{en.title}</span>
                    {en.sub && <span className="l2">{en.sub}</span>}
                  </span>
                  {en.right && (
                    <span className={`palette-right${en.shortcut ? ' shortcut' : ''}`}>
                      {en.right}
                    </span>
                  )}
                </button>
              </div>
            );
          })}
          {!entries.length && (
            <div className="empty">
              {isFetching
                ? 'Hledám…'
                : q.trim().length < 2
                  ? 'Napiš aspoň dva znaky.'
                  : 'Nic nenalezeno.'}
            </div>
          )}
        </div>
        <div className="palette-foot">
          <span>↑↓ pohyb</span>
          <span>Enter otevřít</span>
          <span>Zkratky: n nová faktura · g d doklady · g p platby · ? nápověda</span>
        </div>
      </div>
    </div>
  );
}
